import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Bot,
  Cpu,
  Activity,
  ShieldCheck,
  Zap,
  Check,
  Repeat,
  EyeOff
} from "lucide-react";
import SEO from "../components/SEO";

const PILLAR_ICONS = [Bot, ShieldCheck, Activity];
const PILLAR_IDS = ["issuing", "controls", "observability"];
const TRUST_ICONS = [Cpu, Repeat, EyeOff, Zap];

const AiAgentsPage = () => {
  const { t } = useTranslation();
  const pillars = t("aiAgents.pillars.items", { returnObjects: true });
  const trust = t("aiAgents.trust", { returnObjects: true });
  const steps = t("aiAgents.flow.steps", { returnObjects: true });

  return (
    <div data-testid="ai-agents-page">
      <SEO
        path="/ai-agents"
        title={t("seo.aiAgents.title")}
        description={t("seo.aiAgents.description")}
        keywords={t("seo.aiAgents.keywords")}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Programmable card issuing for AI agents",
          "name": "AI Agent card issuing",
          "provider": { "@type": "Organization", "name": t("brand.name") },
          "areaServed": ["Mexico", "Latin America"],
          "description": t("seo.aiAgents.description"),
          "offers": { "@type": "Offer", "category": "Programmable cards for AI agents" },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "AI Agent Card Capabilities",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Programmatic virtual card issuance" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Granular per-agent spend controls" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Real-time agent transaction observability" } }
            ]
          }
        }}
      />

      {/* Hero */}
      <section style={styles.hero}>
        <div className="container">
          <div style={styles.heroInner}>
            <div style={styles.eyebrow}>{t("aiAgents.hero.eyebrow")}</div>
            <h1 style={styles.heroTitle}>
              {t("aiAgents.hero.titleLine1")}<br />{t("aiAgents.hero.titleLine2")}
            </h1>
            <p style={styles.heroSubtitle}>{t("aiAgents.hero.subtitle")}</p>
            <div style={styles.heroCtaRow}>
              <Link to="/contacto">
                <button className="btn-primary" data-testid="ai-agents-hero-cta">
                  {t("common.talkToExpert")}
                  <ArrowRight size={18} style={{ marginLeft: 8 }} />
                </button>
              </Link>
              <a
                href="#issuing"
                style={styles.secondaryLink}
                data-testid="ai-agents-hero-scroll"
              >
                {t("common.viewHowItWorks")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section style={styles.trustSection}>
        <div className="container">
          <div style={styles.trustGrid}>
            {trust.map((label, i) => {
              const Icon = TRUST_ICONS[i] || ShieldCheck;
              return (
                <div key={i} style={styles.trustItem} data-testid={`ai-agents-trust-${i}`}>
                  <Icon size={18} strokeWidth={1.75} color="#667eea" />
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section style={styles.pillarsSection}>
        <div className="container">
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>{t("aiAgents.pillars.title")}</h2>
            <p style={styles.sectionSubtitle}>{t("aiAgents.pillars.subtitle")}</p>
          </div>

          <div style={styles.pillarsList}>
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || Bot;
              const reverse = i % 2 === 1;
              const id = PILLAR_IDS[i] || `pillar-${i}`;
              return (
                <div
                  key={id}
                  id={id}
                  style={{ ...styles.pillarRow, flexDirection: reverse ? "row-reverse" : "row" }}
                  data-testid={`ai-agents-pillar-${id}`}
                >
                  <div style={styles.pillarText}>
                    <div style={styles.pillarEyebrow}>{p.eyebrow}</div>
                    <h3 style={styles.pillarTitle}>{p.title}</h3>
                    <p style={styles.pillarDescription}>{p.description}</p>
                    <ul style={styles.bulletList}>
                      {p.bullets.map((b, j) => (
                        <li key={j} style={styles.bulletItem}>
                          <Check size={18} color="#f5576c" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div style={styles.pillarVisual}>
                    <div style={styles.pillarOrb}>
                      <Icon size={56} strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Flow */}
      <section style={styles.flowSection}>
        <div className="container">
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitleAlt}>{t("aiAgents.flow.title")}</h2>
            <p style={styles.sectionSubtitle}>{t("aiAgents.flow.subtitle")}</p>
          </div>

          <div style={styles.flowGrid}>
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div style={styles.flowStep} data-testid={`ai-agents-flow-${i + 1}`}>
                  <div style={styles.flowNumber}>{String(i + 1).padStart(2, "0")}</div>
                  <div style={styles.flowTitle}>{s.title}</div>
                  <div style={styles.flowDesc}>{s.desc}</div>
                </div>
                {i < steps.length - 1 && <div style={styles.flowConnector} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <div className="container">
          <div style={styles.ctaInner}>
            <h2 style={styles.ctaTitle}>{t("aiAgents.cta.title")}</h2>
            <p style={styles.ctaSubtitle}>{t("aiAgents.cta.subtitle")}</p>
            <Link to="/contacto">
              <button className="btn-primary" style={styles.ctaButton} data-testid="ai-agents-final-cta">
                {t("common.scheduleADemo")}
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Style object is intentionally identical in shape to StablecoinPage so the
// existing global mobile.css scale governs typography & spacing uniformly.
const styles = {
  hero: { padding: "160px 20px 80px", background: "#ffffff" },
  heroInner: { maxWidth: "900px", margin: "0 auto" },
  eyebrow: { fontSize: "13px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#f5576c", marginBottom: "20px" },
  heroTitle: { fontSize: "64px", fontWeight: 700, lineHeight: 1.05, letterSpacing: "-0.03em", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", marginBottom: "24px" },
  heroSubtitle: { fontSize: "20px", lineHeight: 1.6, color: "#4a5568", maxWidth: "720px", marginBottom: "36px" },
  heroCtaRow: { display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" },
  secondaryLink: { color: "#667eea", textDecoration: "none", fontWeight: 600, fontSize: "16px" },
  trustSection: { padding: "32px 20px", background: "#f8f9fc", borderTop: "1px solid rgba(0,0,0,0.06)", borderBottom: "1px solid rgba(0,0,0,0.06)" },
  trustGrid: { display: "flex", justifyContent: "center", alignItems: "center", gap: "40px", flexWrap: "wrap", maxWidth: "1100px", margin: "0 auto" },
  trustItem: { display: "flex", alignItems: "center", gap: "10px", fontSize: "15px", fontWeight: 500, color: "#2d3748" },
  pillarsSection: { padding: "100px 20px", background: "#ffffff" },
  sectionHeader: { textAlign: "center", maxWidth: "760px", margin: "0 auto 70px" },
  sectionTitle: { fontSize: "52px", fontWeight: 700, lineHeight: 1.1, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", marginBottom: "14px", letterSpacing: "-0.02em" },
  sectionTitleAlt: { fontSize: "44px", fontWeight: 700, color: "#1a202c", lineHeight: 1.15, marginBottom: "14px", letterSpacing: "-0.02em" },
  sectionSubtitle: { fontSize: "18px", color: "#4a5568", lineHeight: 1.6 },
  pillarsList: { display: "flex", flexDirection: "column", gap: "80px", maxWidth: "1200px", margin: "0 auto" },
  pillarRow: { display: "flex", alignItems: "center", gap: "70px" },
  pillarText: { flex: 1.2, minWidth: 0 },
  pillarEyebrow: { fontSize: "12px", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#f5576c", marginBottom: "12px" },
  pillarTitle: { fontSize: "40px", fontWeight: 700, color: "#1a202c", lineHeight: 1.15, letterSpacing: "-0.02em", marginBottom: "18px", whiteSpace: "pre-line" },
  pillarDescription: { fontSize: "17px", color: "#4a5568", lineHeight: 1.7, marginBottom: "22px" },
  bulletList: { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" },
  bulletItem: { display: "flex", alignItems: "flex-start", gap: "12px", fontSize: "15px", color: "#2d3748", lineHeight: 1.55 },
  pillarVisual: { flex: 1, display: "flex", justifyContent: "center" },
  pillarOrb: { width: "260px", height: "260px", borderRadius: "50%", background: "radial-gradient(circle at 30% 30%, rgba(102,126,234,0.18), rgba(118,75,162,0.10) 50%, transparent 70%), linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%)", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 30px 60px -20px rgba(102, 126, 234, 0.25)", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea" },
  flowSection: { padding: "100px 20px", background: "#f8f9fc", borderTop: "1px solid rgba(0,0,0,0.06)" },
  flowGrid: { display: "flex", alignItems: "stretch", justifyContent: "center", gap: "16px", maxWidth: "1100px", margin: "0 auto" },
  flowStep: { flex: 1, background: "#ffffff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "16px", padding: "28px", minWidth: 0 },
  flowConnector: { width: "40px", height: "2px", alignSelf: "center", background: "linear-gradient(90deg, rgba(102,126,234,0.6) 0%, rgba(245,87,108,0.6) 100%)", borderRadius: "2px" },
  flowNumber: { fontSize: "13px", fontWeight: 700, color: "#f5576c", letterSpacing: "0.08em", marginBottom: "10px" },
  flowTitle: { fontSize: "19px", fontWeight: 700, color: "#1a202c", letterSpacing: "-0.01em", marginBottom: "8px", lineHeight: 1.3 },
  flowDesc: { fontSize: "15px", color: "#4a5568", lineHeight: 1.6 },
  ctaSection: { padding: "100px 20px", background: "#ffffff", borderTop: "1px solid rgba(0,0,0,0.08)" },
  ctaInner: { maxWidth: "700px", margin: "0 auto", textAlign: "center" },
  ctaTitle: { fontSize: "52px", fontWeight: 700, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", marginBottom: "20px", letterSpacing: "-0.02em" },
  ctaSubtitle: { fontSize: "19px", color: "#4a5568", lineHeight: 1.6, marginBottom: "36px" },
  ctaButton: { fontSize: "17px", padding: "16px 36px" }
};

// Mobile/tablet refinements specific to this page (mirrors Stablecoin).
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    [data-testid="ai-agents-page"] h1 { font-size: 44px !important; }
    [data-testid="ai-agents-page"] h2 { font-size: 38px !important; }
    [data-testid^="ai-agents-pillar-"] { flex-direction: column !important; gap: 40px !important; text-align: left; }
    [data-testid^="ai-agents-pillar-"] > div:last-child { width: 100%; }
    [data-testid="ai-agents-page"] div.flowGrid { flex-direction: column !important; }
  }
  @media (max-width: 640px) {
    [data-testid="ai-agents-page"] section:first-child { padding: 120px 16px 60px !important; }
    [data-testid="ai-agents-page"] h1 { font-size: 34px !important; }
    [data-testid="ai-agents-page"] h2 { font-size: 28px !important; }
    [data-testid="ai-agents-page"] h3 { font-size: 28px !important; }
    [data-testid="ai-agents-page"] div[style*="width: 260px"][style*="height: 260px"] {
      width: 200px !important; height: 200px !important;
    }
    [data-testid="ai-agents-page"] div.flowGrid { flex-direction: column !important; }
    [data-testid="ai-agents-page"] div[style*="width: 40px"][style*="height: 2px"] {
      width: 2px !important; height: 24px !important;
      background: linear-gradient(180deg, rgba(102,126,234,0.6) 0%, rgba(245,87,108,0.6) 100%) !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default AiAgentsPage;
